import { useState } from 'react'

import { FxButton, FxPanel } from '@/components/fx'
import type { Template } from '@/types/domain'
import { RecordSchemaDialog } from './RecordSchemaDialog'
import {
  contactDefaultSchema,
  parseRecordSchema,
  RECORD_COLUMN_TYPE_LABELS,
  type RecordColumnType,
} from './recordSchema'

/**
 * "Datensatz-Schema" — the columns one analysed document is normalised into
 * by the record check, and therefore the columns of the Datensätze tab and
 * of the Excel file.
 *
 * A template with no schema of its own uses the built-in contact record.
 * That is shown, marked "Standard", rather than left blank: the columns
 * exist either way, and an empty panel would suggest the export has none.
 * The first edit **pins a copy onto this template** — from then on it is
 * this form's own schema and no longer follows the built-in one — which the
 * editor says before it saves, because it is a one-way step.
 */
export function SchemaSection({ template, canWrite }: { template: Template; canWrite: boolean }) {
  const [editing, setEditing] = useState(false)
  const stored = parseRecordSchema(template.recordSchema)
  const schema = stored ?? contactDefaultSchema()
  const isDefault = stored == null

  return (
    <FxPanel
      title="Datensatz-Schema"
      action={
        canWrite ? (
          <FxButton variant="tonal" onClick={() => setEditing(true)}>
            Bearbeiten
          </FxButton>
        ) : undefined
      }
      className="p-5 xs:p-8 sm:p-10"
    >
      <p className="mb-4 mt-0 text-small text-text-muted">
        Das Schema bestimmt die Spalten im Tab Datensätze und in der Excel-Datei.
      </p>

      <p className="mb-3 mt-0 text-body">
        <span className="font-semibold">{schema.name}</span>
        {isDefault && (
          <span className="ml-2 rounded-sm bg-fill px-2 py-0.5 text-small text-text">Standard</span>
        )}
      </p>

      <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
        {schema.columns.map((column) => {
          const type = column.type as RecordColumnType | undefined
          const typeLabel = type ? RECORD_COLUMN_TYPE_LABELS[type] : undefined
          return (
            <li key={column.name} className="rounded-sm bg-fill px-2.5 py-1 text-small text-text">
              {column.name}
              {(typeLabel ?? column.type) && (
                <span className="text-text-muted"> · {typeLabel ?? column.type}</span>
              )}
            </li>
          )
        })}
      </ul>

      {isDefault && (
        <p className="mb-0 mt-4 text-small text-text-muted">
          Dieses Musterformular benutzt das eingebaute Kontakt-Schema. Sobald Sie es bearbeiten,
          bekommt das Formular eine eigene Kopie.
        </p>
      )}

      <RecordSchemaDialog
        open={editing}
        onClose={() => setEditing(false)}
        schema={schema}
        isDefault={isDefault}
      />
    </FxPanel>
  )
}
